import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import {SectionHeader} from '@github-ui/issue-metadata/SectionHeader'
import type {PullRequestPickerPullRequest} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import type {BranchPickerBranch} from '@github-ui/item-picker-branch-relay/BranchPicker'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {ActionList, Banner} from '@primer/react'
import {useCallback, useMemo, useState} from 'react'

import {LABELS} from '../../../constants/labels'
import {TEST_IDS} from '../../../constants/test-ids'
import {VALUES} from '../../../constants/values'
import {useCopilotAgent} from '../../../hooks/use-copilot-agent'
import type {IssueSecondaryNode} from '../../issue-viewer-secondary-query-types'
import {BranchNextStepDesktop} from './BranchNextStepDesktop'
import {BranchNextStepLocal} from './BranchNextStepLocal'
import {CopilotAgentModeButton} from './CopilotAgentModeButton'
import type {BranchNextStep} from './CreateBranchDialog'
import type {LinkedBranchRefNode, LinkedPullRequestReleaseNode} from './development-section-query-types'
import {LazyDevelopmentPickerOnClick} from './DevelopmentPicker'
import styles from './DevelopmentSection.module.css'
import {LinkedBranches} from './LinkedBranches'
import {LinkedPullRequests} from './LinkedPullRequests'
import {NoBranchesOrLinkedPullRequests} from './NoBranchesOrLinkedPullRequests'

// Derived from the generated `IssueViewerSecondaryViewQuery` type (regenerated by `npm run sync`).
// `RelayCompat` widens nullable fields so the still-Relay parent data is assignable; remove it once
// every `DevelopmentSectionFragment` spreader is migrated off Relay. `repository` is narrowed to the
// fields this section selects, because `IssueSecondaryNode['repository']` is the merged superset of
// every secondary fragment's repository selection.
export type DevelopmentSectionFragment = RelayCompat<
  Pick<
    IssueSecondaryNode,
    | 'id'
    | 'title'
    | 'number'
    | 'databaseId'
    | 'viewerCanLinkBranches'
    | 'linkedBranches'
    | 'closedByPullRequestsReferences'
    | 'linkedPullRequestReleases'
  > & {
    repository: Pick<IssueSecondaryNode['repository'], 'id' | 'databaseId' | 'name'> & {
      owner: Pick<IssueSecondaryNode['repository']['owner'], 'login'>
    }
  }
>

export type DevelopmentSectionBaseProps = {
  onSelection: () => void
  readonly: boolean
}

type DevelopmentSectionInternalProps = DevelopmentSectionBaseProps & {
  issue: DevelopmentSectionFragment
}

export function DevelopmentSectionFallback() {
  return (
    <Section
      sectionHeader={<ReadonlySectionHeader title={LABELS.sectionTitles.development} />}
      emptyText={LABELS.emptySections.development}
    />
  )
}

export function DevelopmentSection({issue: data, onSelection, readonly}: DevelopmentSectionInternalProps) {
  const [isCreateBranchDialogOpen, setCreateBranchDialogOpen] = useState(false)

  const {id: issueId, title, number, databaseId, repository} = data
  const readonlyDevelopment = !data.viewerCanLinkBranches || readonly

  const [linkedBranches, linkedPullRequests] = useMemo(
    () => [getLinkedBranches(data), getLinkedPullRequests(data)],
    [data],
  )

  const releaseByPrId = useMemo(() => {
    const map = new Map<string, PullRequestRelease>()
    for (const pair of data.linkedPullRequestReleases?.nodes ?? []) {
      if (pair?.pullRequest?.id && pair?.release) {
        map.set(pair.pullRequest.id, pair.release)
      }
    }
    return map
  }, [data.linkedPullRequestReleases?.nodes])

  const hasSomeData = linkedBranches.length > 0 || linkedPullRequests.length > 0

  const [branchNextStep, setBranchNextStep] = useState<BranchNextStep>('none')
  const [newBranchName, setNewBranchName] = useState<string | null>(null)

  const noBranchesOrLinkedPullRequests = useCallback(
    (linkText: string, before?: string, after?: string) => {
      return (
        <NoBranchesOrLinkedPullRequests
          issueId={issueId}
          title={title}
          number={number}
          owner={repository.owner.login}
          repo={repository.name}
          before={before}
          after={after}
          linkText={linkText}
          reportCreateBranchDialogOpen={setCreateBranchDialogOpen}
          setBranchNextStep={setBranchNextStep}
          setNewBranchName={setNewBranchName}
        />
      )
    },
    [issueId, number, repository.name, repository.owner.login, title],
  )

  const developmentSectionHeader = useMemo(() => {
    if (readonlyDevelopment) {
      return <ReadonlySectionHeader title={LABELS.sectionTitles.development} />
    }

    return (
      <LazyDevelopmentPickerOnClick
        issueId={issueId}
        anchorElement={props => <SectionHeader title={LABELS.sectionTitles.development} buttonProps={props} />}
        repoPickerSubtitle={noBranchesOrLinkedPullRequests(
          LABELS.development.createBranch.toLowerCase(),
          LABELS.development.repoPickerSubtitle,
        )}
        prAndBranchPickerSubtitle={noBranchesOrLinkedPullRequests(
          LABELS.development.createBranch.toLowerCase(),
          LABELS.development.prsBranchesPickerSubtitle,
        )}
        isCreateBranchDialogOpen={isCreateBranchDialogOpen}
        linkedBranches={linkedBranches}
        linkedPullRequests={linkedPullRequests}
        onSelection={onSelection}
      />
    )
  }, [
    readonlyDevelopment,
    issueId,
    noBranchesOrLinkedPullRequests,
    isCreateBranchDialogOpen,
    linkedBranches,
    linkedPullRequests,
    onSelection,
  ])

  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const copilotAgentModeEnabled = useFeatureFlag('copilot_agent_mode')

  const {codespaceCreationError, setCodespaceCreationError} = useCopilotAgent({
    repoId: repository.id,
    issueDatabaseId: databaseId || 0,
  })

  return (
    <Section id={`sidebar-development-section`} sectionHeader={developmentSectionHeader}>
      {copilotAgentModeEnabled && (
        // When the `copilot_agent_mode` feature flag is enabled,
        // display an "Open in Copilot Agent Mode" button that allows users
        // to open the current issue in a Codespace running Agent Mode.
        <div className={styles.developmentSectionContainer}>
          <CopilotAgentModeButton repo={repository} issueId={databaseId ?? 0} onError={setCodespaceCreationError} />
          {codespaceCreationError && (
            <Banner
              title="Codespace creation failed"
              hideTitle
              description={LABELS.development.copilot.error(codespaceCreationError)}
              variant="critical"
              className="mt-2"
            />
          )}
        </div>
      )}
      {!hasSomeData && (
        <span className={styles.developmentHelpText}>
          {!readonlyDevelopment &&
            noBranchesOrLinkedPullRequests(
              LABELS.development.createBranch,
              undefined,
              LABELS.development.createBranchSuffix,
            )}
          <span>{readonlyDevelopment && LABELS.emptySections.development}</span>
        </span>
      )}
      {branchNextStep === 'local' && (
        <BranchNextStepLocal branch={newBranchName} onClose={() => setBranchNextStep('none')} />
      )}
      {branchNextStep === 'desktop' && (
        <BranchNextStepDesktop
          owner={repository.owner.login}
          repository={repository.name}
          branch={newBranchName}
          onClose={() => setBranchNextStep('none')}
        />
      )}
      <ActionList
        variant={'full'}
        data-testid={TEST_IDS.linkedPullRequestContainer}
        className={styles.developmentActionList}
      >
        <LinkedBranches linkedBranches={linkedBranches} />
        <LinkedPullRequests linkedPullRequests={linkedPullRequests} releaseByPrId={releaseByPrId} />
      </ActionList>
    </Section>
  )
}

export type LinkedBranch = {
  name: string
  repositoryWithOwner: string
  repositoryId: string
  id: string
}

// Derived from the generated `IssueViewerSecondaryViewQuery` type (via the inlined `release`
// selection). RELAY-CLEANUP(DevelopmentSection): `RelayCompat` widens nullable fields so the
// still-Relay parent data is assignable. Remove the wrapper when DevelopmentSection leaves Relay.
export type PullRequestRelease = RelayCompat<LinkedPullRequestReleaseNode['release']>

function getLinkedPullRequests(developmentData: DevelopmentSectionFragment): PullRequestPickerPullRequest[] {
  return (
    developmentData.closedByPullRequestsReferences?.nodes
      ?.flatMap(node => {
        if (!node || node.__typename !== 'PullRequest') return []
        return node
      })
      .slice(0, VALUES.maxLinkedPullRequests) || []
  )
}

function getLinkedBranches(developmentData: DevelopmentSectionFragment): BranchPickerBranch[] {
  return (
    developmentData.linkedBranches?.nodes
      ?.flatMap(node => {
        if (!node || !node.ref) return []
        const branch = readRef(node.ref)
        return branch ? branch : []
      })
      .slice(0, VALUES.maxLinkedBranches) || []
  )
}

// Use the generated `Ref` type as-is, but swap both `target`s for a plain `{oid, id}` (all `readRef` ever reads)
type RefTarget = {readonly oid: string; readonly id: string}

export type LinkedBranchRefData = RelayCompat<
  Omit<LinkedBranchRefNode, 'target' | 'repository'> & {
    readonly target: RefTarget | null
    readonly repository: Omit<LinkedBranchRefNode['repository'], 'defaultBranchRef'> & {
      readonly defaultBranchRef:
        | (Omit<NonNullable<LinkedBranchRefNode['repository']['defaultBranchRef']>, 'target'> & {
            readonly target: RefTarget | null
          })
        | null
    }
  }
>

/**
 * Maps a nullable `Ref` target to the non-null `{oid, id}` shape BranchPicker
 * expects, filling in empty-string fallbacks for missing values.
 */
function toTarget(target?: {oid: string; id: string} | null): {oid: string; id: string} {
  return {
    oid: target?.oid || '',
    id: target?.id || '',
  }
}

/**
 * Helper function to map plain linked-branch `Ref` data to a BranchPickerBranch
 */
export function readRef(data: LinkedBranchRefData | null): BranchPickerBranch | null {
  if (!data) return null

  const result: BranchPickerBranch = {
    name: data.name,
    id: data.id,
    repository: {
      id: data.repository.id,
      nameWithOwner: data.repository.nameWithOwner,
      defaultBranchRef: {
        name: data.repository.defaultBranchRef?.name || '',
        id: data.repository.defaultBranchRef?.id || '',
        target: toTarget(data.repository.defaultBranchRef?.target),
        associatedPullRequests: {
          totalCount: data.repository.defaultBranchRef?.associatedPullRequests?.totalCount || 0,
        },
      },
    },
    target: toTarget(data.target),
    associatedPullRequests: {
      totalCount: data.associatedPullRequests?.totalCount || 0,
    },
    __typename: data.__typename,
  }

  return result
}
